import React from 'react';

interface TermsPageProps {
  onNavigate?: (path: string, view: string, tab?: string) => void;
}

export const TermsPage: React.FC<TermsPageProps> = ({ onNavigate }) => {
  const handleNav = (path: string, view: string, tab: string = 'water') => {
    if (onNavigate) {
      onNavigate(path, view, tab);
    } else {
      window.location.href = path;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white dark:bg-[#1F2937] rounded-3xl p-6 sm:p-10 lg:p-12 space-y-8 shadow-xs border border-slate-200 dark:border-slate-700">
        <header className="border-b border-slate-100 dark:border-slate-700/80 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/40 text-[#087F9C] dark:text-[#39BFD3] border border-teal-200/60 dark:border-teal-800/60 inline-block mb-3">
            Terms of Service
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
            Terms and Conditions of Use
          </h1>
          <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-4">
            <span>Effective Date: October 31, 2026</span>
            <span>Last Updated: November 2026</span>
            <span>Applicable to: PipeSizingCalculator.com</span>
          </div>
        </header>

        {/* 1. Agreement to Terms */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            1. Agreement to Terms
          </h2>
          <p>
            By accessing or using PipeSizingCalculator.com (&quot;the Website&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), you agree to be bound by these Terms and Conditions (&quot;Terms&quot;). If you disagree with any part of these Terms, you must not use or access this Website or its engineering calculation tools.
          </p>
        </section>

        {/* 2. Permitted & Acceptable Use */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            2. Permitted &amp; Acceptable Use
          </h2>
          <p>
            PipeSizingCalculator.com provides hydraulic calculation utilities, pipe schedule tables, and engineering guides for educational, preliminary estimation, design verification, and conceptual planning purposes.
          </p>
          <p>You agree to use the Website solely for lawful engineering and academic pursuits. You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>Attempt to disrupt, overload, or impair the website&apos;s infrastructure through denial-of-service attacks or automated scraping scripts.</li>
            <li>Reverse-engineer or attempt to compromise client-side application security.</li>
            <li>Misrepresent calculation outputs generated by the site as official stamped engineering design deliverables or certified regulatory filings without independent professional verification.</li>
          </ul>
        </section>

        {/* 3. Engineering Calculator Usage & User Responsibilities */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            3. Engineering Calculator Usage &amp; User Responsibilities
          </h2>
          <p>
            <strong>Critical User Acknowledgment:</strong> All calculations, velocity evaluations, Reynolds numbers, Darcy friction factor solutions, and pipe dimension lookups generated by our calculators are mathematical approximations based on user-entered values and standard fluid mechanics equations (such as Darcy-Weisbach, Colebrook-White, and Swamee-Jain).
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <strong>Independent Verification Required:</strong> You, as the user, assume full responsibility for verifying all calculation outputs, assumptions, temperature factors, roughness coefficients, and local plumbing code requirements with a qualified, licensed Professional Engineer (PE), Chartered Engineer (CEng), or equivalent local engineering authority before procurement, fabrication, or construction.
            </li>
            <li>
              <strong>Input Accuracy:</strong> You acknowledge that variation in fluid temperature, water quality, pipe wall aging, manufacturing tolerances, fitting geometry, and pressure variations will impact actual field hydraulic performance.
            </li>
          </ul>
        </section>

        {/* 4. Intellectual Property Rights */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            4. Intellectual Property
          </h2>
          <p>
            The website design, visual identity, user interface components, interactive code, graphics, documentation guides, and PDF layout templates are the intellectual property of PipeSizingCalculator.com and are protected by applicable copyright laws.
          </p>
          <p>
            Third-party technical standards referenced on this website—including BS EN 806, CIBSE Guide G, ASME B36.10M, ASTM B88, and ISO standards—are the intellectual property of their respective standards development organizations and are referenced strictly under nominative fair use for technical description and engineering context.
          </p>
        </section>

        {/* 5. Disclaimer of Warranties */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            5. Disclaimer of Warranties
          </h2>
          <p>
            The Website and all calculation tools are provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular engineering purpose, title, and non-infringement.
          </p>
          <p>
            We do not warrant that the calculators will be uninterrupted, error-free, that defects will be corrected immediately, or that the results will meet specific municipal code approvals without professional engineering oversight.
          </p>
        </section>

        {/* 6. Limitation of Liability */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            6. Limitation of Liability
          </h2>
          <p>
            To the fullest extent permitted by applicable law, in no event shall PipeSizingCalculator.com, its operators, contributors, or technical authors be liable for any direct, indirect, incidental, special, consequential, or punitive damages—including but not limited to loss of profits, construction delays, equipment failure, piping burst, water damage, acoustic nuisance, or engineering rework—arising out of or in connection with your access to, use of, or inability to use this Website or reliance on its calculated results.
          </p>
        </section>

        {/* 7. External Links */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            7. Third-Party Links &amp; Advertisements
          </h2>
          <p>
            The Website may contain links to external third-party websites, engineering organizations, or advertisements served by third-party ad networks (such as Google AdSense). We do not endorse, monitor, or control external websites and assume no responsibility for the content, privacy practices, or availability of third-party platforms.
          </p>
        </section>

        {/* 8. Modifications to Terms */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            8. Modifications to Terms
          </h2>
          <p>
            We reserve the right to amend or replace these Terms at any time at our discretion. The revised Terms become effective immediately upon posting. Your continued use of the Website after any modifications indicates your acceptance of the updated Terms.
          </p>
        </section>

        {/* Navigation Quick Links */}
        <footer className="pt-6 border-t border-slate-100 dark:border-slate-700/80 flex flex-wrap gap-3 text-xs font-semibold">
          <button
            onClick={() => handleNav('/', 'calculators', 'water')}
            className="px-4 py-2.5 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700 transition-colors"
          >
            Launch Water Pipe Sizing &rarr;
          </button>
          <button
            onClick={() => handleNav('/privacy-policy/', 'privacy')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Privacy Policy &rarr;
          </button>
          <button
            onClick={() => handleNav('/disclaimer/', 'disclaimer')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Engineering Disclaimer &rarr;
          </button>
        </footer>
      </article>
    </div>
  );
};
